import { useCallback, useMemo, useEffect, useRef } from 'react';
import intl from 'react-intl-universal';
import { debounce } from 'lodash';
import { TaskWithAssignees } from 'types/store/projectTasksList';
import { TaskListElement } from 'types/store/tasksList';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { useAppThunkDispatch } from 'state/store';
import {
  updateTaskDetail,
  setUpdateTaskStatus,
  fetchAvailableAssignees,
  selectAvailableAssignees,
  selectAvailableAssigneesStatus,
} from 'state/TaskDetail/taskSlice';
import { TaskWithAssigneesAndProject } from 'types/store/tasksList';
import { SLICE_STATUS, TASK_STATUS } from 'utils/constants';
import { formatDate } from 'Pages/TaskDetailPage/TaskDetailForm/helpers/helpers';
import { StatusOption } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';
import { TaskAssignee } from 'types/store/tasks';
import { useSelector } from 'react-redux';

interface Props {
  canUpdateTask: boolean;
  canUpdateStatus?: boolean;
  isViewOnly: boolean;
  task: TaskWithAssignees | TaskWithAssigneesAndProject;
  updateTaskInList: (
    taskId: string,
    updatedTask: Partial<TaskListElement>
  ) => void;
  isAssigneesDropdownOpened?: boolean;
  updateAssignees?: (updatedAssignees: TaskAssignee[]) => void;
}

interface UseUpdateCellsReturn {
  handleSaveEstimateHours: (newValue: number) => void;
  handleSaveTaskStatus: (newValue: string) => void;
  handleSaveAssignees: (newValue: string) => void;
  handleSearchAssignees: (searchTerm: string) => void;
  statuses: StatusOption[];
  availableAssignees: TaskAssignee[];
  isFetchingAvailableAssignees: boolean;
}

export const useUpdateCells = ({
  isAssigneesDropdownOpened,
  canUpdateTask,
  canUpdateStatus,
  isViewOnly,
  task,
  updateTaskInList,
  updateAssignees,
}: Props): UseUpdateCellsReturn => {
  const dispatch = useAppThunkDispatch();
  const { id, estimateHours } = task;

  const availableAssignees = useSelector(selectAvailableAssignees);
  const availableAssigneesStatus = useSelector(selectAvailableAssigneesStatus);

  useEffect(() => {
    if (!isAssigneesDropdownOpened) return;

    dispatch(
      fetchAvailableAssignees({
        taskId: id,
        includeDisabled: true,
        limit: 1000,
      })
    );
  }, [id, dispatch, isAssigneesDropdownOpened]);

  const handleSaveEstimateHours = useCallback(
    async (newValue: number) => {
      if (!canUpdateTask || isViewOnly) return;
      if (newValue === parseFloat(estimateHours || '0')) return;

      try {
        const res = await dispatch(
          updateTaskDetail({
            task: {
              id,
              estimateHours: newValue.toString(),
            },
          })
        ).unwrap();

        const taskUpdates: Partial<TaskListElement> = {
          ...res,
          assignees: res.assignees?.map((assignee) => assignee.id) || [],
          projectId: res.project?.id || '',
        };

        updateTaskInList(id, taskUpdates);

        dispatch(setUpdateTaskStatus(undefined));
      } catch (error) {
        dispatch(setUpdateTaskStatus(undefined));
        dispatch(
          showNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get(
              'TASKS.ESTIMATED_HOURS_UPDATE_ERROR.TITLE'
            ),
            notificationMessage: intl.get(
              'TASKS.ESTIMATED_HOURS_UPDATE_ERROR.MESSAGE'
            ),
          })
        );
      }
    },
    [dispatch, estimateHours, canUpdateTask, isViewOnly, id, updateTaskInList]
  );

  const handleSaveTaskStatus = useCallback(
    async (newValue: string) => {
      if (!canUpdateStatus || newValue === task.status) return;

      try {
        const taskToUpdate: {
          id: string;
          status: string;
          completionDate?: string;
        } = {
          id,
          status: newValue,
        };

        if (
          newValue === TASK_STATUS.COMPLETED &&
          task.status !== TASK_STATUS.COMPLETED
        ) {
          taskToUpdate.completionDate = formatDate(new Date());
        }

        const res = await dispatch(
          updateTaskDetail({
            task: taskToUpdate,
          })
        ).unwrap();

        const taskUpdates: Partial<TaskListElement> = {
          ...res,
          assignees: res.assignees?.map((assignee) => assignee.id) || [],
          projectId: res.project?.id || '',
        };

        updateTaskInList(id, taskUpdates);

        if (
          newValue === TASK_STATUS.COMPLETED &&
          task.status !== TASK_STATUS.COMPLETED &&
          taskToUpdate.completionDate
        ) {
          dispatch(
            showNotification({
              notificationVariant: 'success',
              notificationTitle: intl.get(
                'TASKS.TASK_DETAIL_PAGE.COMPLETION_TASK_TOOLTIP_TITLE'
              ),
              notificationMessage: intl.get(
                'TASKS.TASK_DETAIL_PAGE.COMPLETION_DATE_AUTO_UPDATED'
              ),
            })
          );
        }
        dispatch(setUpdateTaskStatus(undefined));
      } catch (error) {
        dispatch(setUpdateTaskStatus(undefined));
        dispatch(
          showNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get('TASKS.TASK_STATUS_UPDATE_ERROR.TITLE'),
            notificationMessage: intl.get(
              'TASKS.TASK_STATUS_UPDATE_ERROR.MESSAGE'
            ),
          })
        );
      }
    },
    [dispatch, task.status, id, updateTaskInList, canUpdateStatus]
  );

  const handleSaveAssignees = useCallback(
    async (updatedAssigneeId: string) => {
      if (!canUpdateTask || isViewOnly) return;

      const isCurrentlyAssigned = task.assignees
        .map((assignee) => assignee.id)
        .includes(updatedAssigneeId);

      try {
        const res = await dispatch(
          updateTaskDetail({
            task: {
              id,
              assignees: {
                toAdd: !isCurrentlyAssigned ? [updatedAssigneeId] : [],
                toRemove: isCurrentlyAssigned ? [updatedAssigneeId] : [],
              },
            },
          })
        ).unwrap();

        if (res.assignees && res.assignees.length > 0) {
          updateAssignees?.(res.assignees);
        }

        const taskUpdates: Partial<TaskListElement> = {
          ...res,
          assignees: res.assignees?.map((assignee) => assignee.id) || [],
          projectId: res.project?.id || '',
        };

        updateTaskInList(id, taskUpdates);

        dispatch(setUpdateTaskStatus(undefined));
      } catch (error) {
        dispatch(setUpdateTaskStatus(undefined));
        dispatch(
          showNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get(
              'TASKS.TASK_ASSIGNEE_UPDATE_ERROR.TITLE'
            ),
            notificationMessage: intl.get(
              'TASKS.TASK_ASSIGNEE_UPDATE_ERROR.MESSAGE'
            ),
          })
        );
      }
    },
    [
      dispatch,
      task.assignees,
      canUpdateTask,
      isViewOnly,
      id,
      updateTaskInList,
      updateAssignees,
    ]
  );

  const debouncedSearchAssignees = useRef(
    debounce((taskId: string, searchTerm: string, dispatchFn: any) => {
      dispatchFn(
        fetchAvailableAssignees({
          taskId,
          assigneeName: searchTerm,
          includeDisabled: true,
          limit: 1000,
        })
      );
    }, 500)
  ).current;

  const handleSearchAssignees = useCallback(
    (searchTerm: string) => {
      debouncedSearchAssignees(id, searchTerm, dispatch);
    },
    [id, dispatch, debouncedSearchAssignees]
  );

  const statuses = useMemo(() => {
    return Object.values(TASK_STATUS).map((status) => ({
      label: intl.get(
        `TASKS.TASK_DETAIL_PAGE.STATUS_OPTIONS.${status.toUpperCase()}`
      ),
      value: status,
      disabled: status === TASK_STATUS.COMPLETED && task.assignees.length === 0,
      message: intl.get('TASKS.TASK_ASSIGNEE_REQUIRED_TOOLTIP'),
    }));
  }, [task.assignees.length]);

  return {
    handleSaveEstimateHours,
    handleSaveTaskStatus,
    handleSaveAssignees,
    handleSearchAssignees,
    statuses,
    availableAssignees,
    isFetchingAvailableAssignees:
      availableAssigneesStatus === SLICE_STATUS.LOADING,
  };
};
